import type { IncidentAssignment, IncidentAssignmentRoleName } from '../Types/IncidentAssignment';

/** Ordered list of assignment roles for display. */
const ROLE_ORDER: IncidentAssignmentRoleName[] = [
  'IncidentCommander',
  'OfficeDirector',
  'Headcounter',
  'BackupHeadcounter',
];

const ROLE_LABELS: Record<IncidentAssignmentRoleName, string> = {
  IncidentCommander: 'Incident Commander',
  OfficeDirector: 'Office Director',
  Headcounter: 'Headcounter',
  BackupHeadcounter: 'Backup Headcounter',
};

const ROLE_LABELS_PLURAL: Record<IncidentAssignmentRoleName, string> = {
  IncidentCommander: 'Incident Commanders',
  OfficeDirector: 'Office Directors',
  Headcounter: 'Headcounters',
  BackupHeadcounter: 'Backup Headcounters',
};

const ROLE_NUMBER_MAP: Record<number, IncidentAssignmentRoleName> = {
  0: 'IncidentCommander',
  3: 'OfficeDirector',
  1: 'Headcounter',
  2: 'BackupHeadcounter',
};

/**
 * Returns the human-readable label for an assignment role.
 * Accepts either the string roleName or numeric roleNumber.
 */
export const getAssignmentRoleLabel = (
  roleName?: string,
  roleNumber?: number,
): string => {
  const key = resolveRoleName(roleName, roleNumber);
  return key ? ROLE_LABELS[key] : roleName ?? 'Unknown Role';
};

/**
 * Returns the sort order index for a role (lower = first).
 */
export const getAssignmentRoleSortOrder = (
  roleName?: string,
  roleNumber?: number,
): number => {
  const key = resolveRoleName(roleName, roleNumber);
  if (!key) return 999;
  const idx = ROLE_ORDER.indexOf(key);
  return idx >= 0 ? idx : 999;
};

/**
 * Groups assignments by role, returning entries in display order.
 */
export const groupIncidentAssignmentsByRole = (
  assignments: IncidentAssignment[],
): { roleName: IncidentAssignmentRoleName; label: string; assignments: IncidentAssignment[] }[] => {
  const map = new Map<IncidentAssignmentRoleName, IncidentAssignment[]>();

  for (const a of assignments) {
    const key = resolveRoleName(a.assignmentRoleName, a.assignmentRole) ?? a.assignmentRoleName;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(a);
  }

  return ROLE_ORDER
    .filter((role) => map.has(role))
    .map((role) => ({
      roleName: role,
      label: ROLE_LABELS[role],
      assignments: map.get(role)!,
    }));
};

/**
 * Formats a compact summary string from assignments.
 * e.g. "2 Incident Commanders · 1 Office Director · 14 Headcounters · 3 Backups"
 * Returns "No incident team assigned" for empty input.
 */
export const formatTeamSummary = (assignments: IncidentAssignment[]): string => {
  const active = assignments.filter((a) => a.isActive);
  if (active.length === 0) return 'No incident team assigned';

  const groups = groupIncidentAssignmentsByRole(active);
  return groups
    .map((g) => {
      const count = g.assignments.length;
      if (g.roleName === 'BackupHeadcounter') {
        return `${count} ${count === 1 ? 'Backup' : 'Backups'}`;
      }
      const label = count === 1 ? ROLE_LABELS[g.roleName] : ROLE_LABELS_PLURAL[g.roleName];
      return `${count} ${label}`;
    })
    .join(' · ');
};

/** Pluralize a role label based on count. */
export const pluralizeRoleLabel = (
  roleName: IncidentAssignmentRoleName,
  count: number,
): string => {
  if (roleName === 'BackupHeadcounter') {
    return count === 1 ? 'Backup Headcounter' : 'Backup Headcounters';
  }
  return count === 1 ? ROLE_LABELS[roleName] : ROLE_LABELS_PLURAL[roleName];
};

const ROLE_SUBTITLES: Record<IncidentAssignmentRoleName, string> = {
  IncidentCommander: 'Primary incident leadership',
  OfficeDirector: 'Office-level incident oversight',
  Headcounter: 'People assigned to mark roster status',
  BackupHeadcounter: 'Backup support for headcount operations',
};

/** Returns a short description for a role section header. */
export const getRoleSubtitle = (roleName: IncidentAssignmentRoleName): string =>
  ROLE_SUBTITLES[roleName] ?? '';

// ── internal helpers ──

function resolveRoleName(
  roleName?: string,
  roleNumber?: number,
): IncidentAssignmentRoleName | undefined {
  if (roleName && roleName in ROLE_LABELS) return roleName as IncidentAssignmentRoleName;
  if (roleNumber !== undefined && roleNumber in ROLE_NUMBER_MAP) return ROLE_NUMBER_MAP[roleNumber];
  return undefined;
}
